// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * A college's onboarding detail, read only when an operator asks for it.
 *
 * **One request, on open.** The list already carries every column the table
 * renders, so a per-row detail read would be an N+1 against the control plane
 * that answered nothing the table was waiting for. The query here is `enabled`
 * by the dialog being open and keyed by the college, so opening two colleges in
 * turn makes exactly two requests and reopening one makes none.
 *
 * **What this dialog cannot show.** `CollegeOnboardingView` has no activation
 * digest, no bound public key, no thumbprint, no replay identifier, and no
 * attempt counter — the validator refuses an unexpected member outright rather
 * than stripping it, and the BFF refuses one before that. So the guarantee is
 * structural rather than a matter of which fields this file happens to read:
 * there is nothing here to render. `safe_reason` is the single free-text field,
 * and it is bounded operator-facing text produced by Platform for this purpose.
 *
 * A refusal is presented through the same sanitised vocabulary as every other
 * lifecycle failure. An operator learns that the read was refused, never why
 * the service decided so (`API-012`).
 */

import {
  queryKeys,
  StateBadge,
  useApiClients,
  useSession,
  type CollegeOnboardingView,
} from '@eai/ui-shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import {
  ACTIVATION_STATE_PRESENTATION,
  describeExpiry,
  describeLifecycleFailure,
  describeOnboarding,
  describeReadinessEvidence,
  describeReadinessOutcome,
  formatInstant,
  maskedTail,
} from './presentation';

/** Props for {@link OnboardingDetailsDialog}. */
export interface OnboardingDetailsDialogProps {
  /** The college whose onboarding is being inspected. */
  readonly tenantId: string;
  /** The college's name, for the dialog's accessible name. */
  readonly collegeLabel: string;
  /** Close the dialog. */
  readonly onClose: () => void;
}

const TITLE_ID = 'onboarding-details-title';

/** One labelled fact in the detail list. */
function Fact({ term, children }: { term: string; children: ReactNode }): ReactNode {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, py: 0.5 }}>
      <Typography component="dt" variant="body2" color="text.secondary" sx={{ minWidth: 220 }}>
        {term}
      </Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0 }}>
        {children}
      </Typography>
    </Box>
  );
}

/** The resolved detail, rendered. */
function OnboardingFacts({ detail }: { detail: CollegeOnboardingView }): ReactNode {
  const onboarding = describeOnboarding(detail.onboarding);
  const activation = ACTIVATION_STATE_PRESENTATION[detail.activation.state];
  const tail = maskedTail(detail.activation);

  return (
    <Box component="dl" sx={{ m: 0 }} data-testid="onboarding-facts">
      <Fact term="Onboarding state">
        <StateBadge label={onboarding.label} tone={onboarding.tone} subject="Onboarding" />
      </Fact>
      <Fact term="Requested generation">{detail.onboarding.requested_generation}</Fact>
      <Fact term="Observed generation">
        {detail.onboarding.observed_generation ?? 'Not reported'}
      </Fact>
      <Fact term="Readiness evidence">{describeReadinessEvidence(detail.onboarding)}</Fact>
      <Fact term="Readiness outcome">{describeReadinessOutcome(detail.onboarding)}</Fact>
      <Fact term="Reported reason">{detail.onboarding.safe_reason ?? 'None reported'}</Fact>
      <Fact term="Activation state">
        <StateBadge label={activation.label} tone={activation.tone} subject="Activation code" />
      </Fact>
      <Fact term="Activation code">{tail ?? 'No code issued'}</Fact>
      <Fact term="Activation expiry">{describeExpiry(detail.activation)}</Fact>
      <Fact term="Claimed">{formatInstant(detail.activation.claimed_at, 'Not claimed')}</Fact>
    </Box>
  );
}

/** Read and show one college's onboarding state. */
export function OnboardingDetailsDialog({
  tenantId,
  collegeLabel,
  onClose,
}: OnboardingDetailsDialogProps): ReactNode {
  const clients = useApiClients();
  const { scope } = useSession();

  const query = useQuery({
    queryKey: queryKeys.controlPlane.collegeOnboarding(scope, tenantId),
    queryFn: ({ signal }) => clients.controlPlane.readCollegeOnboarding({ tenantId, signal }),
    // A refusal is not a transient fault. Repeating it adds load and hides the
    // real state (`RUN-010`).
    retry: false,
  });

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby={TITLE_ID}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
    >
      <DialogTitle id={TITLE_ID}>Onboarding details for {collegeLabel}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Registration, activation, and onboarding are separate lifecycles. A claimed activation
          code does not make a college active; only verified readiness evidence does.
        </Typography>

        {query.isPending ? (
          <Stack direction="row" spacing={2} alignItems="center" role="status" aria-live="polite">
            <CircularProgress size={20} aria-hidden="true" />
            <Typography variant="body2">Loading onboarding details</Typography>
          </Stack>
        ) : null}

        {query.isError ? (
          <Alert severity="error" role="alert" data-testid="onboarding-error">
            {describeLifecycleFailure(query.error, 'onboarding')}
          </Alert>
        ) : null}

        {query.isSuccess ? <OnboardingFacts detail={query.data} /> : null}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
